import { useEffect, useState } from "react";
import { VestigingFilter } from "../components/VestigingFilter";
import { AiHulpKnop, AiTariefHulp, useAiBeschikbaar } from "../components/AiTariefHulp";
import { useVestigingKeuze } from "../components/useVestigingKeuze";
import { BtwKeuze, btwWeergave, huidigeBtwUitHistorie, useBtwCodes } from "../components/BtwKeuze";
import { CatalogusLijst, WeergaveKeuze, type Weergave } from "../components/CatalogusLijst";
import { leesWeergave, bewaarWeergave } from "../components/CatalogusKop";
import { Ambulance } from "lucide-react";
import { OverviewHeader } from "../components/OverviewHeader";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  Button,
  Field,
  Modal,
  StatusBadge,
  TextareaField,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// tarief-types Epic 5 (Story 5.1): the Dierenambulance tarief overview — the React port of the Jmix
// `DierenambulanceTariefOverviewView`, the smallest tariff type, migrated AS-IS. A single rates grid
// (Diensttype / Basisprijs / Per-KM / Geldig vanaf) with two row actions:
//   • Prijs wijzigen — opens a modal (basisprijs + per-km + effectdatum + reden) → POST /{id}/prijswijziging.
//   • Historie tonen — opens a modal listing all tarief_versie rows (GET /{id}/historie) with status badges.
// The Jmix "Tarief Toevoegen" and "Historie-overzicht" toolbar buttons are DISABLED stubs ("volgt in
// vervolgwerk") — reproduced here as disabled buttons with a "volgt" tooltip. The legacy spoedtoeslag
// (bedrag_tarief_3) is NOT carried on tarief_versie and is intentionally left out, as in Jmix.
//
// All endpoints use RELATIVE urls (the dataProvider prepends /api once; a leading slash doubles to
// /api/api → 404).

interface DierenambulanceTariefRow {
  tariefId: number;
  vestigingId: number | null;
  diensttype: string;
  basisprijs: number | null;
  perKmTarief: number | null;
  geldigVanaf: string | null;
}

interface TariefHistorieRegel {
  versieId: number;
  bedrag: number;
  bedragHoog: number | null;
  effectdatum: string;
  einddatum: string | null;
  reden: string | null;
  btwCodeId: number | null;
  status: "GEPLAND" | "ACTIEF" | "VERVALLEN";
}

// ── helpers ──────────────────────────────────────────────────────────────────────────────────────
type SaveNotice = "forbidden" | "forbiddenPast" | "error" | null;
const statusToErr = (err: unknown, isPast = false): SaveNotice => {
  const code = (err as { statusCode?: number }).statusCode;
  if (code === 403) return isPast ? "forbiddenPast" : "forbidden";
  return "error";
};
const num = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function historieTone(status: TariefHistorieRegel["status"]): StatusTone {
  if (status === "ACTIEF") return "success";
  if (status === "GEPLAND") return "warning";
  return "neutral";
}

// ── the page ─────────────────────────────────────────────────────────────────────────────────────
export function DierenambulanceTariefOverview() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // Source parity: the "Geldig op" peildatum filter (empty = today, the api default).
  const [peildatum, setPeildatum] = useState("");
  const [zoek, setZoek] = useState("");
  const { data, isLoading, isError, refetch } = useCustom<DierenambulanceTariefRow[]>({
    url: peildatum ? `dierenambulance-tarief?peildatum=${encodeURIComponent(peildatum)}` : "dierenambulance-tarief",
    method: "get",
  });
  // Het bereik waarover je kijkt: alles, alleen wat organisatiebreed geldt, of alleen wat een
  // vestiging zelf heeft ingesteld. Client-side, want de rijen dragen hun vestiging al mee.
  // Dezelfde vestigingkeuze als op Producten & diensten en het factuuroverzicht.
  const vestiging = useVestigingKeuze();
  const [weergave, setWeergave] = useState<Weergave>(() => leesWeergave("fawkes.ambulanceTarief"));
  const kiesWeergave = (w: Weergave) => {
    setWeergave(w);
    bewaarWeergave("fawkes.ambulanceTarief", w);
  };
  const binnenVestiging = (data?.data ?? []).filter((r) => vestiging.hoortErbij(r));
  // Geen soort-tegels op dit scherm: elke rij ís al één diensttype, dus elke tegel zou er precies
  // één bevatten. De vestigingkeuze in de kop is hier de enige zinnige indeling.
  // Twintig ambulancediensten in één lijst: zoeken op naam is hier het filter dat je echt gebruikt.
  const rows = zoek.trim()
    ? binnenVestiging.filter((r) => r.diensttype.toLowerCase().includes(zoek.trim().toLowerCase()))
    : binnenVestiging;

  const [aiOpen, setAiOpen] = useState(false);
  const aiBeschikbaar = useAiBeschikbaar("dierenambulance-tarief/ai-suggestie");
  const [prijsRow, setPrijsRow] = useState<DierenambulanceTariefRow | null>(null);
  const [historieRow, setHistorieRow] = useState<DierenambulanceTariefRow | null>(null);

  const columns: Column<DierenambulanceTariefRow>[] = [
    { header: t("dierenambulanceTarief.col.diensttype"), cell: (r) => r.diensttype },
    {
      header: t("dierenambulanceTarief.col.basisprijs"),
      align: "right",
      cell: (r) => (r.basisprijs != null ? formatEuro(r.basisprijs, locale) : t("dierenambulanceTarief.none")),
    },
    {
      header: t("dierenambulanceTarief.col.perKm"),
      align: "right",
      cell: (r) => (r.perKmTarief != null ? formatEuro(r.perKmTarief, locale) : t("dierenambulanceTarief.none")),
    },
    {
      header: t("dierenambulanceTarief.col.geldigVanaf"),
      cell: (r) => (r.geldigVanaf ? formatDate(r.geldigVanaf, locale) : t("dierenambulanceTarief.none")),
    },
    {
      header: t("dierenambulanceTarief.col.acties"),
      cell: (r) => (
        <div className="asiel-tarief__row-actions">
          <button type="button" className="asiel-tarief__link" onClick={() => setPrijsRow(r)}>
            {t("dierenambulanceTarief.action.prijswijzigen")}
          </button>
          <button type="button" className="asiel-tarief__link" onClick={() => setHistorieRow(r)}>
            {t("dierenambulanceTarief.action.historie")}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="product-overview">
      <OverviewHeader
        icon={<Ambulance size={24} />}
        title={t("menu.dierenambulanceTarief")}
      />
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          {/* Disabled stubs — faithful to the Jmix view ("volgt in vervolgwerk"). */}
          <Button disabled title={t("dierenambulanceTarief.stub.tooltip")}>
            {t("dierenambulanceTarief.action.toevoegen")}
          </Button>
          <Button variant="secondary" disabled title={t("dierenambulanceTarief.stub.tooltip")}>
            {t("dierenambulanceTarief.action.historieOverzicht")}
          </Button>
          <VestigingFilter
            label={t("financieel.filter.vestiging")}
            opties={vestiging.opties}
            gekozen={vestiging.gekozen}
            onChange={vestiging.setGekozen}
          />
          <Field
            label={t("dierenambulanceTarief.search")}
            placeholder={t("dierenambulanceTarief.search")}
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
          />
          <Field
            label={t("dierenambulanceTarief.peildatum")}
            type="date"
            value={peildatum}
            onChange={(e) => setPeildatum(e.target.value)}
          />
          <span className="asiel-tarief__toolbar-spacer" />
          {aiBeschikbaar && <AiHulpKnop onClick={() => setAiOpen(true)} />}
          <Button variant="secondary" onClick={() => refetch()}>
            {t("dierenambulanceTarief.action.refresh")}
          </Button>
        </div>

        <div className="fawkes-catalogus__lijstkop">
          <span className="fawkes-muted">
            {rows.length}{" "}{t(rows.length === 1 ? "dierAanwezig.results.foundOne" : "dierAanwezig.results.found")}
          </span>
          <WeergaveKeuze
            weergave={weergave}
            onKies={kiesWeergave}
            labels={{
              groep: t("catalogus.weergave"),
              kaart: t("catalogus.weergave.kaart"),
              compact: t("catalogus.weergave.compact"),
              lijst: t("catalogus.weergave.lijst"),
            }}
          />
        </div>

        <p className="asiel-tarief__cond-empty">{t("dierenambulanceTarief.banner")}</p>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("dierenambulanceTarief.load.error")}
          </div>
        )}

        <CatalogusLijst<DierenambulanceTariefRow>
          columns={columns}
          rows={rows}
          getRowKey={(r) => String(r.tariefId)}
          empty={isLoading ? t("common.loading") : t("dierenambulanceTarief.empty")}
          weergave={weergave}
        />
      </div>

      {aiOpen && (
        <AiTariefHulp
          pad="dierenambulance-tarief/ai-suggestie"
          voorbeeld={t("tariefAi.voorbeeld.ambulance")}
          vandaag={todayIso()}
          onClose={() => setAiOpen(false)}
          onBewerk={(tariefId) => {
            const rij = rows.find((r) => String(r.tariefId) === tariefId);
            if (rij) { setAiOpen(false); setPrijsRow(rij); }
          }}
        />
      )}
      {prijsRow && (
        <PrijsWijzigingDialog
          row={prijsRow}
          onClose={() => setPrijsRow(null)}
          onSaved={() => {
            setPrijsRow(null);
            refetch();
          }}
        />
      )}

      {historieRow && (
        <HistorieDialog
          row={historieRow}
          locale={locale}
          onClose={() => setHistorieRow(null)}
        />
      )}
    </div>
  );
}

// ── Prijs wijzigen ─────────────────────────────────────────────────────────────────────────────────
function PrijsWijzigingDialog(props: {
  row: DierenambulanceTariefRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { row, onClose, onSaved } = props;
  const t = useTranslate();
  const [bedrag, setBedrag] = useState(row.basisprijs != null ? String(row.basisprijs) : "");
  const [bedragHoog, setBedragHoog] = useState(row.perKmTarief != null ? String(row.perKmTarief) : "");
  const [effectdatum, setEffectdatum] = useState(todayIso());
  const [reden, setReden] = useState("");
  // BTW is verplicht per versie. Voorgevuld met die van de lopende versie (uit de historie): wie alleen
  // de prijs aanpast, hoeft hem niet opnieuw op te zoeken. Heeft het tarief nog nergens BTW, dan blijft
  // het veld leeg en moet er gekozen worden.
  const btwCodes = useBtwCodes();
  const { data: histData } = useCustom<TariefHistorieRegel[]>({
    url: `dierenambulance-tarief/${row.tariefId}/historie`,
    method: "get",
    queryOptions: { retry: false },
  });
  const huidigeBtw = huidigeBtwUitHistorie(Array.isArray(histData?.data) ? histData!.data : []);
  const [btwCodeId, setBtwCodeId] = useState("");
  useEffect(() => {
    if (huidigeBtw !== "") setBtwCodeId((b) => (b === "" ? huidigeBtw : b));
  }, [huidigeBtw]);

  const [bedragMissing, setBedragMissing] = useState(false);
  const [btwMissing, setBtwMissing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice>(null);
  const { mutate, isLoading: saving } = useCustomMutation();

  const submit = () => {
    const prijs = num(bedrag);
    setBedragMissing(prijs == null);
    setBtwMissing(btwCodeId === "");
    if (prijs == null || btwCodeId === "") return;
    setNotice(null);

    const isPast = effectdatum !== "" && effectdatum < todayIso();
    mutate(
      {
        url: `dierenambulance-tarief/${row.tariefId}/prijswijziging`,
        method: "post",
        values: {
          bedrag: prijs,
          bedragHoog: num(bedragHoog),
          effectdatum: effectdatum === "" ? null : effectdatum,
          reden: reden.trim() === "" ? null : reden.trim(),
          btwCodeId: Number(btwCodeId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onSaved(),
        onError: (err) => setNotice(statusToErr(err, isPast)),
      },
    );
  };

  return (
    <Modal
      open
      title={t("dierenambulanceTarief.prijswijzigen.title", { args: [row.diensttype] })}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {t("dierenambulanceTarief.prijswijzigen.submit")}
          </Button>
        </>
      }
    >
      <div className="asiel-tarief__form">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`dierenambulanceTarief.notice.${notice}`)}
          </div>
        )}
        <Field
          label={t("dierenambulanceTarief.field.basisprijs")}
          value={bedrag}
          inputMode="decimal"
          onChange={(e) => setBedrag(e.target.value)}
        />
        {bedragMissing && (
          <div className="asiel-tarief__field-error">{t("dierenambulanceTarief.field.basisprijs.required")}</div>
        )}
        <Field
          label={t("dierenambulanceTarief.field.perKm")}
          value={bedragHoog}
          inputMode="decimal"
          onChange={(e) => setBedragHoog(e.target.value)}
        />
        <Field
          label={t("dierenambulanceTarief.field.effectdatum")}
          type="date"
          value={effectdatum}
          onChange={(e) => setEffectdatum(e.target.value)}
        />
        <BtwKeuze
          value={btwCodeId}
          onChange={(v) => { setBtwCodeId(v); setBtwMissing(false); }}
          codes={btwCodes}
          missing={btwMissing}
          t={t}
        />
        <TextareaField
          label={t("dierenambulanceTarief.field.reden")}
          value={reden}
          rows={2}
          onChange={(e) => setReden(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// ── Historie tonen ───────────────────────────────────────────────────────────────────────────────
function HistorieDialog(props: {
  row: DierenambulanceTariefRow;
  locale: string;
  onClose: () => void;
}) {
  const { row, locale, onClose } = props;
  const t = useTranslate();

  const { data, isLoading, isError } = useCustom<TariefHistorieRegel[]>({
    url: `dierenambulance-tarief/${row.tariefId}/historie`,
    method: "get",
  });
  const regels = data?.data ?? [];
  const btwCodes = useBtwCodes();

  const columns: Column<TariefHistorieRegel>[] = [
    { header: t("dierenambulanceTarief.hist.vanaf"), cell: (r) => formatDate(r.effectdatum, locale) },
    {
      header: t("dierenambulanceTarief.hist.tot"),
      cell: (r) => (r.einddatum ? formatDate(r.einddatum, locale) : t("dierenambulanceTarief.none")),
    },
    {
      header: t("dierenambulanceTarief.hist.basisprijs"),
      align: "right",
      cell: (r) => formatEuro(r.bedrag, locale),
    },
    {
      header: t("dierenambulanceTarief.hist.perKm"),
      align: "right",
      cell: (r) => (r.bedragHoog != null ? formatEuro(r.bedragHoog, locale) : t("dierenambulanceTarief.none")),
    },
    { header: t("btw.kolom"), align: "right", cell: (r) => btwWeergave(btwCodes, r.btwCodeId) },
    { header: t("dierenambulanceTarief.hist.reden"), cell: (r) => r.reden ?? t("dierenambulanceTarief.none") },
    {
      header: t("dierenambulanceTarief.hist.status"),
      cell: (r) => (
        <StatusBadge tone={historieTone(r.status)}>
          {t(`dierenambulanceTarief.hist.status.${r.status}`)}
        </StatusBadge>
      ),
    },
  ];

  return (
    <Modal
      open
      size="wide"
      title={t("dierenambulanceTarief.historie.title", { args: [row.diensttype] })}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t("common.close")}
        </Button>
      }
    >
      {isError ? (
        <div className="asiel-tarief__notice asiel-tarief__notice--error">
          {t("dierenambulanceTarief.historie.error")}
        </div>
      ) : (
        <IndeelbareTabel<TariefHistorieRegel> tabelKey="dierenambulance-tarief-overview"
          columns={columns}
          rows={regels}
          getRowKey={(r) => String(r.versieId)}
          empty={isLoading ? t("common.loading") : t("dierenambulanceTarief.historie.empty")}
        />
      )}
    </Modal>
  );
}
